import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
  Chip   // Used to render each selected date as a removable tag
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { uwinAPI } from '../../../services/uwin.api';

const CreateVaccinationSession = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get session site data from navigation state
  const sessionSiteData = location.state?.sessionSite || {};
console.log(sessionSiteData,"sessionSiteData")
  // Session types state — filtered to only show types relevant to this session site
  const [sessionTypes, setSessionTypes] = useState([]);
  const [loadingSessionTypes, setLoadingSessionTypes] = useState(true);

  // Existing sessions fetched from API — used to block already-taken dates per session type
  const [existingSessions, setExistingSessions] = useState([]);

  // Controls whether the date picker calendar is open
  // Kept as local state so we can keep it open after each date pick
  const [calendarOpen, setCalendarOpen] = useState(false);
  
  // Initial form values state - will be updated when session types load
  const [initialValues, setInitialValues] = useState({
    sessionSiteName: sessionSiteData.name || '',
    uipSessionType: '',
    uipSessionTypeId: null,
    selectedDates: [],
    startTime: null,
    endTime: null,
    totalCapacity: ''
  });

  useEffect(() => {
    const fetchSessionDetails = async () => {
      try {
        const response = await uwinAPI.getSessionDetails({ session_site_id: sessionSiteData?.session_site_id });
        if (response?.success && Array.isArray(response.data)) {
          setExistingSessions(response.data);
        }
      } catch (error) {
        console.error('Error fetching session details:', error);
      }
    };
    fetchSessionDetails();
  }, [])
  // Fetch master session types from API, then filter to only the types
  // that belong to this session site (passed via navigation state)
  const fetchSessionTypes = async () => {
    try {
      setLoadingSessionTypes(true);

      // Names allowed for this session site e.g. ["RI - Children And Pregnant Women", "IMI Campaign"]
      const allowedTypeNames = sessionSiteData.uip_session_type || [];

      const response = await uwinAPI.getUIPSessionType();
      console.log('Master session types API response:', response);

      if (response && response.data && Array.isArray(response.data)) {
        // Filter master list to only include types whose name exists in allowedTypeNames
        const filtered = response.data.filter(
          (type) => allowedTypeNames.includes(type.uip_session_type_name)
        );

        setSessionTypes(filtered);

        // Default the dropdown to the first filtered type
        if (filtered.length > 0) {
          setInitialValues(prev => ({
            ...prev,
            uipSessionType: filtered[0].uip_session_type_name,
            uipSessionTypeId: filtered[0].uip_session_type
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching session types:', error);
      toast.error('Error fetching session types');
    } finally {
      setLoadingSessionTypes(false);
    }
  };

  // Fetch and filter session types on component mount
  useEffect(() => {
    fetchSessionTypes();
  }, []);

  // Yup Validation Schema
  const validationSchema = Yup.object({
    uipSessionTypeId: Yup.number()
      .required('Please select a session type')
      .nullable(),
    
    selectedDates: Yup.array()
      .min(1, 'Please select at least one date')
      .required('Please select at least one date'),
    
    startTime: Yup.mixed()
      .required('Please select start time')
      .nullable(),
    
    endTime: Yup.mixed()
      .required('Please select end time')
      .nullable()
      .test('time-validation', 'Start time should not be greater than end time', function(value) {
        const { startTime } = this.parent;
        if (!startTime || !value) return true; // Skip validation if either time is not set
        return startTime.isBefore(value) || startTime.isSame(value);
      }),
    totalCapacity: Yup.number()
      .required('Please enter beneficiary capacity')
      .positive('Beneficiary capacity must be greater than 0')
      .integer('Beneficiary capacity must be a whole number')
  });

  // Handle back navigation
  const handleBack = () => {
    navigate(-1);
  };

  // Format time from dayjs to required format (e.g., "9AM", "6:15PM")
  const formatTime = (dayjsTime) => {
    if (!dayjsTime) return '';
    
    const hour = dayjsTime.hour();
    const minute = dayjsTime.minute();
    const isPM = hour >= 12;
    
    // Convert to 12-hour format
    let hour12 = hour % 12;
    if (hour12 === 0) hour12 = 12; // Handle midnight/noon
    
    // Format: "9AM" or "6:15PM"
    if (minute === 0) {
      return `${hour12}${isPM ? 'PM' : 'AM'}`;
    } else {
      return `${hour12}:${minute.toString().padStart(2, '0')}${isPM ? 'PM' : 'AM'}`;
    }
  };

  // Format date from dayjs to required format (YYYY-MM-DD)
  const formatDate = (dayjsDate) => {
    if (!dayjsDate) return '';
    return dayjsDate.format('YYYY-MM-DD');
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // Get the encrypted session_site_id from sessionSiteData
      const encryptedSiteId = sessionSiteData.encryptedId || sessionSiteData.session_site_id;
      
      if (!encryptedSiteId) {
        toast.error('Session site information is missing. Please go back and select a site again.');
        return;
      }

      // Prepare payload according to API specification
      const payload = {
        uip_session_type: values.uipSessionTypeId,
        dates: values.selectedDates.map(date => formatDate(date)),
        start_time: formatTime(values.startTime),
        end_time: formatTime(values.endTime),
        beneficiary_capacity: parseInt(values.totalCapacity),
        session_site_id: encryptedSiteId,
        session_status: "0" // Default status as per API spec
      };

      console.log('Submitting payload:', payload);

      // Call the API
      const response = await uwinAPI.sessionCreation(payload);

      console.log('API Response:', response);

      // Check if response is successful
      if (response.success) {
        toast.success('Session created successfully!');
        // Navigate back to the list page
        navigate(-1);
      } else {
        handleAPIError(response);
      }
    } catch (error) {
      console.error('Error creating session:', error);
      handleAPIError(error.response?.data || error);
    } finally {
      setSubmitting(false);
    }
  };

// Handle API error responses and display as toast
const handleAPIError = (errorData) => {
  if (errorData?.message?.error) {
    toast.error(errorData.message.error );
  } else if (errorData?.message) {
    toast.error("Failed to create session. Please try again.");
  } else {
    toast.error('Failed to create session. Please try again.');
  }
};

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh', py: 3 }}>
        <Container maxWidth="xl">
          {/* Header Section with Back Button */}
          <Box sx={{ mb: 3 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{
                color: '#1976d2',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '14px',
                mb: 2,
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline'
                }
              }}
            >
              Back to UIP Vaccination Session Sites
            </Button>
            
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600, 
                color: '#183b56'
              }}
            >
              Create Vaccination Session
            </Typography>
          </Box>

          {/* Main Form with Formik */}
          <Paper sx={{ p: 4 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                color: '#183b56',
                mb: 3
              }}
            >
              Session Details
            </Typography>

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize // Allow form to reinitialize when initial values change
            >
              {({ values, errors, touched, setFieldValue, setFieldTouched, isSubmitting }) => (
                <Form>
                  {/* Session Site Name - Disabled */}
                  <Box sx={{ mb: 3, maxWidth: '600px' }}>
                    <Typography 
                      component="label" 
                      sx={{ 
                        display: 'block',
                        mb: 1,
                        fontWeight: 500,
                        fontSize: '14px',
                        color: '#183b56'
                      }}
                    >
                      Session Site Name
                    </Typography>
                    <TextField
                      fullWidth
                      value={values.sessionSiteName}
                      disabled
                      size="small"
                      sx={{
                        '& .MuiInputBase-input.Mui-disabled': {
                          WebkitTextFillColor: '#5a7184',
                          backgroundColor: '#f5f5f5'
                        }
                      }}
                    />
                  </Box>

                  {/* UIP Session Type */}
                  <Box sx={{ mb: 3, maxWidth: '600px' }}>
                    <Typography 
                      component="label" 
                      sx={{ 
                        display: 'block',
                        mb: 1,
                        fontWeight: 500,
                        fontSize: '14px',
                        color: '#183b56'
                      }}
                    >
                      UIP Session Type
                    </Typography>
                    <FormControl 
                      fullWidth 
                      size="small"
                      error={touched.uipSessionTypeId && Boolean(errors.uipSessionTypeId)}
                    >
                      <Select
                        value={values.uipSessionType}
                        onChange={(e) => {
                          const selectedTypeName = e.target.value;
                          const selectedType = sessionTypes.find(
                            type => type.uip_session_type_name === selectedTypeName
                          );
                          setFieldValue('uipSessionType', selectedTypeName);
                          setFieldValue('uipSessionTypeId', selectedType?.uip_session_type || null);

                          // Compute blocked dates for the newly selected type
                          const blockedForNewType = existingSessions
                            .filter(s => s.uip_session_category === selectedTypeName)
                            .map(s => dayjs(s.date_of_session).startOf('day'));

                          // Remove any already-selected dates that are now blocked
                          const cleanedDates = values.selectedDates.filter(
                            d => !blockedForNewType.some(blocked => blocked.isSame(d.startOf('day'), 'day'))
                          );
                          setFieldValue('selectedDates', cleanedDates);
                        }}
                        onBlur={() => setFieldTouched('uipSessionTypeId', true)}
                        displayEmpty
                        disabled={loadingSessionTypes}
                      >
                        {loadingSessionTypes ? (
                          <MenuItem value="">Loading...</MenuItem>
                        ) : (
                          sessionTypes.map((type) => (
                            <MenuItem 
                              key={type.uip_session_type} 
                              value={type.uip_session_type_name}
                            >
                              {type.uip_session_type_name}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                      {touched.uipSessionTypeId && errors.uipSessionTypeId && (
                        <FormHelperText error>
                          {errors.uipSessionTypeId}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Box>

                  {/* Date Picker - Multi-select with toggle behaviour */}
                  <Box sx={{ mb: 3, maxWidth: '600px' }}>
                    <Typography
                      component="label"
                      sx={{
                        display: 'block',
                        mb: 1,
                        fontWeight: 500,
                        fontSize: '14px',
                        color: '#183b56'
                      }}
                    >
                      Date
                    </Typography>

                   <DatePicker
                      value={null} // 1. Keep the DatePicker's internal state explicitly empty
                      open={calendarOpen}
                      onOpen={() => setCalendarOpen(true)}
                      onClose={() => setCalendarOpen(false)}
                      minDate={dayjs()} // Disable past dates
                      shouldDisableDate={(day) => {
                        // Block dates that already have a session of the currently selected type
                        return existingSessions
                          .filter(s => s.uip_session_category === values.uipSessionType)
                          .some(s => dayjs(s.date_of_session).startOf('day').isSame(day.startOf('day'), 'day'));
                      }}
                      onChange={(newValue) => {
                        if (!newValue) return;

                        const incoming = newValue.startOf('day');

                        // Check whether this date is already in the list
                        const alreadySelected = values.selectedDates.some(
                          (d) => d.startOf('day').isSame(incoming, 'day')
                        );

                        let updated;
                        if (alreadySelected) {
                          // Deselect: remove the matching date
                          updated = values.selectedDates.filter(
                            (d) => !d.startOf('day').isSame(incoming, 'day')
                          );
                        } else {
                          // Select: append and sort chronologically
                          updated = [...values.selectedDates, incoming].sort(
                            (a, b) => a.valueOf() - b.valueOf()
                          );
                        }

                        setFieldValue('selectedDates', updated);
                        setFieldTouched('selectedDates', true);

                        // Keep the calendar open for the next pick
                        setCalendarOpen(true);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          // 2. Use placeholder instead of value to display the summary
                          placeholder: values.selectedDates.length > 0
                            ? `${values.selectedDates.length} date${values.selectedDates.length > 1 ? 's' : ''} selected`
                            : 'Click to select dates',
                          // Ensure the placeholder looks like normal text when dates are selected
                          sx: {
                            '& .MuiInputBase-input::placeholder': {
                              color: values.selectedDates.length > 0 ? '#183b56' : undefined,
                              opacity: values.selectedDates.length > 0 ? 1 : undefined,
                            }
                          },
                          // 3. REMOVE the `value` property from here!
                          inputProps: { readOnly: true }, // Prevent manual keyboard input
                          error: touched.selectedDates && Boolean(errors.selectedDates),
                          helperText: touched.selectedDates && errors.selectedDates,
                          onBlur: () => setFieldTouched('selectedDates', true),
                          onClick: () => setCalendarOpen(true) // Open on text field click
                        },
                        day: {
                          sx: (theme) => ({
                            '&.multi-selected-day': {
                              backgroundColor: theme?.palette?.primary?.main ?? '#1976d2',
                              color: '#fff',
                              borderRadius: '50%',
                              '&:hover': {
                                backgroundColor: theme?.palette?.primary?.dark ?? '#1565c0'
                              }
                            }
                          })
                        }
                      }}
                      slots={{
                        day: (dayProps) => {
                          const { day, outsideCurrentMonth, ...rest } = dayProps;

                          // Check if this calendar day is in the selected list
                          const isSelected = values.selectedDates.some(
                            (d) => d.startOf('day').isSame(day.startOf('day'), 'day')
                          );

                          // Check if this day is already booked for the selected session type
                          const isBooked = existingSessions
                            .filter(s => s.uip_session_category === values.uipSessionType)
                            .some(s => dayjs(s.date_of_session).startOf('day').isSame(day.startOf('day'), 'day'));

                          return (
                            <Box
                              component="div"
                              onClick={(e) => {
                                // Prevent double-fire from the picker's own onClick
                                e.stopPropagation();
                              }}
                            >
                              <Box
                                component="button"
                                type="button"
                                disabled={rest.disabled}
                                onClick={() => {
                                  if (outsideCurrentMonth || rest.disabled) return;

                                  const incoming = day.startOf('day');
                                  const alreadySelected = values.selectedDates.some(
                                    (d) => d.startOf('day').isSame(incoming, 'day')
                                  );

                                  let updated;
                                  if (alreadySelected) {
                                    updated = values.selectedDates.filter(
                                      (d) => !d.startOf('day').isSame(incoming, 'day')
                                    );
                                  } else {
                                    updated = [...values.selectedDates, incoming].sort(
                                      (a, b) => a.valueOf() - b.valueOf()
                                    );
                                  }

                                  setFieldValue('selectedDates', updated);
                                  setFieldTouched('selectedDates', true);
                                  setCalendarOpen(true); 
                                }}
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: '50%',
                                  border: isBooked ? '1.5px solid #e53935' : 'none',
                                  cursor: rest.disabled ? 'not-allowed' : 'pointer',
                                  fontSize: '0.875rem',
                                  fontWeight: isSelected ? 600 : isBooked ? 500 : 400,
                                  backgroundColor: isSelected
                                    ? '#1976d2'
                                    : isBooked
                                    ? '#ffebee'
                                    : 'transparent',
                                  color: outsideCurrentMonth
                                    ? '#bdbdbd'
                                    : isSelected
                                    ? '#fff'
                                    : isBooked
                                    ? '#c62828'
                                    : rest.disabled
                                    ? '#bdbdbd'
                                    : 'inherit',
                                  position: 'relative',
                                  '&:hover': {
                                    backgroundColor:
                                      !rest.disabled && !isSelected && !isBooked
                                        ? '#e3f2fd'
                                        : isSelected
                                        ? '#1565c0'
                                        : isBooked
                                        ? '#ffcdd2'
                                        : 'transparent'
                                  }
                                }}
                              >
                                {day.date()}
                                {/* Small dot below booked dates */}
                                {isBooked && (
                                  <Box
                                    component="span"
                                    sx={{
                                      position: 'absolute',
                                      bottom: 2,
                                      left: '50%',
                                      transform: 'translateX(-50%)',
                                      width: 4,
                                      height: 4,
                                      borderRadius: '50%',
                                      backgroundColor: '#e53935',
                                      display: 'block'
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                          );
                        }
                      }}
                    />

                    {/* Selected dates rendered as removable chips, sorted chronologically */}
                    {values.selectedDates.length > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1,
                          mt: 1.5
                        }}
                      >
                        {values.selectedDates.map((date) => (
                          <Chip
                            key={date.format('YYYY-MM-DD')} // Stable key per date
                            label={date.format('ddd, DD MMM YYYY')} // e.g. "Mon, 17 Feb 2026"
                            onDelete={() => {
                              // Remove this date from the selection
                              const updated = values.selectedDates.filter(
                                (d) => !d.startOf('day').isSame(date.startOf('day'), 'day')
                              );
                              setFieldValue('selectedDates', updated);
                              setFieldTouched('selectedDates', true);
                            }}
                            size="small"
                            sx={{
                              backgroundColor: '#e3f2fd',
                              color: '#1565c0',
                              fontWeight: 500,
                              fontSize: '12px',
                              '& .MuiChip-deleteIcon': {
                                color: '#1565c0',
                                '&:hover': { color: '#d32f2f' }
                              }
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>

                  {/* Start Time and End Time */}
                  <Box sx={{ mb: 3, maxWidth: '600px' }}>
                    <Stack direction="row" spacing={2}>
                      {/* Start Time */}
                      <Box sx={{ flex: 1 }}>
                        <Typography 
                          component="label" 
                          sx={{ 
                            display: 'block',
                            mb: 1,
                            fontWeight: 500,
                            fontSize: '14px',
                            color: '#183b56'
                          }}
                        >
                          Start Time
                        </Typography>
                        <TimePicker
                          value={values.startTime}
                          onChange={(newValue) => {
                            setFieldValue('startTime', newValue);
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: 'small',
                              placeholder: 'Select start time',
                              error: touched.startTime && Boolean(errors.startTime),
                              helperText: touched.startTime && errors.startTime,
                              onBlur: () => setFieldTouched('startTime', true)
                            }
                          }}
                        />
                      </Box>

                      {/* End Time */}
                      <Box sx={{ flex: 1 }}>
                        <Typography 
                          component="label" 
                          sx={{ 
                            display: 'block',
                            mb: 1,
                            fontWeight: 500,
                            fontSize: '14px',
                            color: '#183b56'
                          }}
                        >
                          End Time
                        </Typography>
                        <TimePicker
                          value={values.endTime}
                          onChange={(newValue) => {
                            setFieldValue('endTime', newValue);
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: 'small',
                              placeholder: 'Select end time',
                              error: touched.endTime && Boolean(errors.endTime),
                              helperText: touched.endTime && errors.endTime,
                              onBlur: () => setFieldTouched('endTime', true)
                            }
                          }}
                        />
                      </Box>
                    </Stack>
                  </Box>

                  {/* Total Beneficiary Capacity */}
                  <Box sx={{ mb: 4, maxWidth: '300px' }}>
                    <Typography 
                      component="label" 
                      sx={{ 
                        display: 'block',
                        mb: 1,
                        fontWeight: 500,
                        fontSize: '14px',
                        color: '#183b56'
                      }}
                    >
                      Total Beneficiary Capacity
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      placeholder="Enter beneficiary capacity"
                      value={values.totalCapacity}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow positive integers
                        if (value === '' || (Number(value) >= 0 && Number.isInteger(Number(value)))) {
                          setFieldValue('totalCapacity', value);
                        }
                      }}
                      onBlur={() => setFieldTouched('totalCapacity', true)}
                      size="small"
                      inputProps={{
                        min: 1,
                        step: 1,
                        onKeyPress: (e) => {
                          // Prevent decimal point and negative sign
                          if (e.key === '.' || e.key === '-' || e.key === 'e' || e.key === 'E') {
                            e.preventDefault();
                          }
                        }
                      }}
                      error={touched.totalCapacity && Boolean(errors.totalCapacity)}
                      helperText={touched.totalCapacity && errors.totalCapacity}
                    />
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ maxWidth: '600px', textAlign: 'right' }}>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          backgroundColor: '#1976d2',
                          minWidth: '140px',
                          '&:hover': {
                            backgroundColor: '#1565c0'
                          },
                          '&.Mui-disabled': {
                            backgroundColor: '#e0e0e0',
                            color: '#9e9e9e'
                          }
                        }}
                      >
                        {isSubmitting ? 'Publishing...' : 'Publish'}
                      </Button>
                    </Stack>
                  </Box>
                </Form>
              )}
            </Formik>
          </Paper>
        </Container>
      </Box>
    </LocalizationProvider>
  );
};

export default CreateVaccinationSession;